import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import { horaCorta, normalizar } from '@/shared/utilidades/formato'
import { IconoChat } from '@/shared/ui/Iconos/Iconos'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import {
  useConversaciones,
  useMandoDeLaConversacion,
  useMensajes,
  useResponderAMano,
  type Conversacion,
} from './useChat'
import estilos from './PaginaChatbot.module.css'

/** Cuánto se muestra "escribiendo…" tras un mensaje entrante sin respuesta. */
const ESPERA_RESPUESTA = 150_000

/**
 * Chatbot de WhatsApp · una ventana de chat, como WhatsApp Web.
 *
 * Columna de conversaciones a la izquierda, la conversación abierta a la
 * derecha, con las burbujas apiladas y separadores de fecha. Los mensajes los
 * guarda el webhook (los entrantes) y la función de envío (los salientes); aquí
 * solo se ven, y llegan en tiempo real (realtime de wa_mensajes). El envío del
 * acta NO vive en esta pantalla: lo dispara el trabajador desde su flujo.
 */
export function PaginaChatbot() {
  const [numero, setNumero] = useState<string | null>(null)
  const { conversaciones, cargando, error } = useConversaciones(numero)
  const [busqueda, setBusqueda] = useState('')

  // Qué chats ya vio el usuario: numero → último_en visto. Un chat abierto se
  // marca como visto con cada mensaje nuevo que entra mientras está abierto.
  const [vistos, setVistos] = useState<Record<string, string>>({})
  useEffect(() => {
    if (!numero) return
    const c = conversaciones.find((c) => c.numero === numero)
    if (c) setVistos((v) => (v[numero] === c.ultimoEn ? v : { ...v, [numero]: c.ultimoEn }))
  }, [numero, conversaciones])

  const noLeido = (c: Conversacion) =>
    c.numero !== numero &&
    c.ultimaDireccion === 'entrante' &&
    (!vistos[c.numero] || c.ultimoEn > vistos[c.numero])

  const filtradas = useMemo(() => {
    const q = normalizar(busqueda)
    if (!q) return conversaciones
    return conversaciones.filter(
      (c) => normalizar(c.titulo).includes(q) || c.numero.includes(busqueda.replace(/\D/g, '')),
    )
  }, [conversaciones, busqueda])

  const activa = conversaciones.find((c) => c.numero === numero) ?? null

  if (cargando) return <Cargando mensaje="CONECTANDO ASISTENTE VIRTUAL..." />
  if (error) return <Aviso tipo="error">{error}</Aviso>

  return (
    <div className={`${estilos.ventana} ${numero ? estilos.enHilo : ''}`}>
      <aside className={estilos.panelChats}>
        <div className={estilos.buscador}>
          <input
            type="search"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar un chat"
            aria-label="Buscar un chat"
          />
        </div>

        <div className={estilos.chats}>
          {conversaciones.length === 0 && (
            <p className={estilos.vacioLista}>
              Aún no hay conversaciones. Cuando salga un acta o un cliente escriba, el chat aparece aquí.
            </p>
          )}
          {filtradas.map((c) => {
            const nuevo = noLeido(c)
            return (
              <button
                key={c.numero}
                className={`${estilos.chatItem} ${c.numero === numero ? estilos.chatSel : ''}`}
                onClick={() => setNumero(c.numero)}
              >
                <span className={estilos.avatar}>{inicial(c.titulo)}</span>
                <span className={estilos.chatDatos}>
                  <span className={estilos.chatTop}>
                    <span className={estilos.chatTitulo}>{c.titulo}</span>
                    <span className={`${estilos.chatHora} ${nuevo ? estilos.horaNueva : ''}`}>
                      {horaCorta(c.ultimoEn)}
                    </span>
                  </span>
                  <span className={estilos.chatBottom}>
                    <span className={`${estilos.chatUltimo} ${nuevo ? estilos.ultimoNuevo : ''}`}>
                      {c.ultimaDireccion === 'saliente' && <span className={estilos.tu}>Tú: </span>}
                      {c.ultimoTexto ?? ''}
                    </span>
                    {nuevo && <span className={estilos.badge} aria-label="Mensajes nuevos" />}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </aside>

      <section className={estilos.panelHilo}>
        {activa ? (
          <Hilo conversacion={activa} onVolver={() => setNumero(null)} />
        ) : (
          <div className={estilos.bienvenida}>
            {/* El icono del chatbot, el mismo del menú, en vez de un emoji:
                el emoji lo dibuja cada sistema a su manera y aquí salía una
                nube morada que no es de esta casa. */}
            <IconoChat className={estilos.bienvenidaIcono} />
            <p>Elige un chat a la izquierda para ver la conversación.</p>
          </div>
        )}
      </section>
    </div>
  )
}

function Hilo({
  conversacion,
  onVolver,
}: {
  conversacion: Conversacion
  onVolver: () => void
}) {
  const { numero, titulo } = conversacion
  const { mensajes, cargando } = useMensajes(numero)
  const fin = useRef<HTMLDivElement>(null)

  // "escribiendo…": si el último mensaje es del cliente (entrante), el bot está
  // por responder. Se muestra hasta que llegue la respuesta (realtime) o pasen
  // 45 s (por si esa conversación ya no espera respuesta).
  const ultimo = mensajes[mensajes.length - 1]
  const [escribiendo, setEscribiendo] = useState(false)
  useEffect(() => {
    if (ultimo && ultimo.direccion === 'entrante') {
      // Solo si el mensaje del cliente es reciente: así no sale "escribiendo…"
      // al abrir un chat viejo que terminó sin respuesta del bot.
      // La ventana tiene que cubrir lo que de verdad tarda el asistente: con
      // fotos y varios mensajes seguidos se ha medido cerca de un minuto, así
      // que 45 s apagaba los puntos ANTES de que llegara la respuesta.
      const edad = Date.now() - new Date(ultimo.creadoEn).getTime()
      if (edad < ESPERA_RESPUESTA) {
        setEscribiendo(true)
        const t = setTimeout(() => setEscribiendo(false), ESPERA_RESPUESTA - edad)
        return () => clearTimeout(t)
      }
    }
    setEscribiendo(false)
  }, [ultimo?.id, ultimo?.direccion, ultimo?.creadoEn])

  useEffect(() => {
    fin.current?.scrollIntoView({ block: 'end' })
  }, [mensajes.length, escribiendo])

  let diaAnterior = ''

  return (
    <>
      <header className={estilos.hiloCabecera}>
        <button className={estilos.volver} onClick={onVolver} aria-label="Volver a los chats">
          ‹
        </button>
        <span className={estilos.avatar}>{inicial(titulo)}</span>
        <span className={estilos.hiloDatos}>
          <span className={estilos.hiloTitulo}>{titulo}</span>
          <span className={`${estilos.hiloEstado} ${escribiendo ? estilos.hiloEscribiendo : ''}`}>
            {escribiendo ? 'escribiendo…' : `+${numero}`}
          </span>
        </span>
      </header>

      <div className={estilos.lienzo}>
        <div className={estilos.mensajes}>
          {cargando && <Cargando compacto mensaje="CONECTANDO ASISTENTE VIRTUAL..." />}
          {mensajes.map((m) => {
            const dia = claveDia(m.creadoEn)
            const separa = dia !== diaAnterior
            diaAnterior = dia
            return (
              <Fragment key={m.id}>
                {separa && (
                  <div className={estilos.separadorFecha}>
                    <span>{etiquetaDia(m.creadoEn)}</span>
                  </div>
                )}
                <div
                  className={`${estilos.burbuja} ${m.direccion === 'saliente' ? estilos.saliente : estilos.entrante}`}
                >
                  <span className={estilos.texto}>{m.texto}</span>
                  <span className={estilos.marca}>{horaCorta(m.creadoEn)}</span>
                </div>
              </Fragment>
            )
          })}
          {escribiendo && (
            <div className={`${estilos.burbuja} ${estilos.entrante} ${estilos.escribiendo}`} aria-label="El asistente está escribiendo">
              <span className={estilos.puntos}>
                <i />
                <i />
                <i />
              </span>
            </div>
          )}
          <div ref={fin} />
        </div>
      </div>

      {/* FUERA DEL LIENZO, NO DENTRO.
          Estaba dentro del área que hace scroll, así que quedaba pegada al
          último mensaje —a media pantalla si la conversación era corta— y
          debajo sobraba el fondo vacío. Aquí es hermana del lienzo: los
          mensajes ocupan lo que haya y la caja se queda abajo, como en
          cualquier chat. */}
      <Responder conversacion={conversacion} />
    </>
  )
}

/**
 * RESPONDER A MANO.
 *
 * La caja solo sale dentro de la ventana de 24 horas, que es la regla de
 * WhatsApp y no nuestra: pasado ese rato solo se puede escribir con plantilla
 * aprobada. Fuera de la ventana se dice por qué, en vez de ofrecer una caja
 * que fallaría al enviar.
 *
 * Escribir TOMA la conversación y calla al asistente; devolvérsela es un botón
 * aparte, para que sea un gesto y no un descuido. A los clientes el asistente
 * nunca les responde, así que ahí no hay nada que callar y no se ofrece
 * devolver.
 */
function Responder({ conversacion }: { conversacion: Conversacion }) {
  const [texto, setTexto] = useState('')
  const responder = useResponderAMano()
  const mando = useMandoDeLaConversacion()

  const { numero, sePuedeEscribir, atendidaPor, atendidaPorNombre, esDeLaCasa } = conversacion

  async function enviar() {
    const limpio = texto.trim()
    if (!limpio) return
    await responder.mutateAsync({ numero, texto: limpio })
    setTexto('')
  }

  if (!sePuedeEscribir) {
    return (
      <div className={estilos.responder}>
        <p className={estilos.fueraDeVentana}>
          Pasaron más de 24 horas desde su último mensaje. WhatsApp solo deja escribir primero
          con una plantilla aprobada.
        </p>
      </div>
    )
  }

  return (
    <div className={estilos.responder}>
      {atendidaPor && (
        <p className={estilos.tomada}>
          {esDeLaCasa
            ? `La atiende ${atendidaPorNombre ?? 'alguien'}: el asistente no responde mientras tanto.`
            : `La atiende ${atendidaPorNombre ?? 'alguien'}.`}{' '}
          {esDeLaCasa && (
            <button
              type="button"
              className={estilos.devolver}
              onClick={() => void mando.mutateAsync({ numero, tomar: false })}
              disabled={mando.isPending}
            >
              Devolver al asistente
            </button>
          )}
        </p>
      )}

      <div className={estilos.cajaResponder}>
        <textarea
          className={estilos.entrada}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            // Enter manda; Mayús+Enter hace salto de línea, como en WhatsApp.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              void enviar()
            }
          }}
          placeholder="Escriba su respuesta…"
          rows={2}
          aria-label="Responder a mano"
        />
        <button
          type="button"
          className={estilos.enviar}
          onClick={() => void enviar()}
          disabled={!texto.trim() || responder.isPending}
        >
          {responder.isPending ? 'Enviando…' : 'Enviar'}
        </button>
      </div>

      {responder.error && (
        <p className={estilos.fallo}>{(responder.error as Error).message}</p>
      )}
    </div>
  )
}

function inicial(texto: string): string {
  const t = (texto ?? '').trim()
  return t ? t[0].toUpperCase() : '·'
}

/** Clave de día (año-mes-día) para saber cuándo cambia la fecha entre mensajes. */
function claveDia(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/** Etiqueta del separador de fecha, como WhatsApp: HOY, AYER o la fecha larga. */
function etiquetaDia(iso: string): string {
  const d = new Date(iso)
  const hoy = new Date()
  const ayer = new Date()
  ayer.setDate(hoy.getDate() - 1)
  const mismo = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  if (mismo(d, hoy)) return 'HOY'
  if (mismo(d, ayer)) return 'AYER'
  return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'long', year: 'numeric' }).format(d)
}
